import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { API_URL } from '../config/api';
import './BlogPost.css';

const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPost();
    }, [slug]);

    const fetchPost = async () => {
        try {
            const response = await fetch(`${API_URL}/blog-posts/${slug}`);
            const data = await response.json();

            if (data.success && data.data) {
                setPost(data.data);
                // Update page title
                document.title = `${data.data.title} | GeneCenter Blog`;
            } else {
                setError('Artículo no encontrado');
            }
        } catch (err) {
            console.error('Error fetching post:', err);
            setError('Error al cargar el artículo');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: post.title,
                text: post.excerpt,
                url: window.location.href
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Enlace copiado al portapapeles');
        }
    };

    if (loading) {
        return (
            <div className="blog-post-page">
                <div className="container">
                    <div className="loading-skeleton">
                        <div className="skeleton-title"></div>
                        <div className="skeleton-meta"></div>
                        <div className="skeleton-image"></div>
                        <div className="skeleton-content"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="blog-post-page">
                <div className="container">
                    <div className="error-state">
                        <h2>😕 {error || 'Artículo no encontrado'}</h2>
                        <p>El artículo que buscas no existe o ha sido eliminado.</p>
                        <Link to="/blog" className="btn btn-primary">
                            <ArrowLeft size={16} /> Volver al Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-post-page">
            {/* Hero with cover image */}
            {post.cover_image_url && (
                <div className="post-hero">
                    <img src={post.cover_image_url} alt={post.title} />
                    <div className="hero-overlay"></div>
                </div>
            )}

            <div className="container">
                <article className="post-article">
                    {/* Back link */}
                    <Link to="/blog" className="back-link">
                        <ArrowLeft size={16} /> Volver al Blog
                    </Link>

                    {/* Category */}
                    {post.category && (
                        <span className="post-category">{post.category}</span>
                    )}

                    {/* Title */}
                    <h1 className="post-title">{post.title}</h1>

                    {/* Meta info */}
                    <div className="post-meta">
                        <span className="post-date">
                            <Calendar size={16} />
                            {formatDate(post.published_at)}
                        </span>
                        {post.author_name && (
                            <span className="post-author">
                                <User size={16} />
                                {post.author_name}
                            </span>
                        )}
                        <button className="share-btn" onClick={handleShare}>
                            <Share2 size={16} /> Compartir
                        </button>
                    </div>

                    {/* Excerpt */}
                    {post.excerpt && (
                        <p className="post-excerpt">{post.excerpt}</p>
                    )}

                    {/* Content */}
                    <div
                        className="post-content"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
                    />

                    {/* Footer */}
                    <div className="post-footer">
                        <Link to="/blog" className="btn btn-secondary">
                            <ArrowLeft size={16} /> Ver más artículos
                        </Link>
                    </div>
                </article>
            </div>
        </div>
    );
};

export default BlogPost;
